import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { 
  Upload, Search, Trash2, Edit2, X, Image, Download, 
  Grid, List, Filter, Tag, FolderOpen, Eye, Copy, Check,
  ShieldCheck, Archive
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MediaLibrary = ({ user }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState([]);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [selectedFile, setSelectedFile] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(null);
  
  // Upload form state
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [uploadCategory, setUploadCategory] = useState('product');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadTags, setUploadTags] = useState('');
  const [recovering, setRecovering] = useState(false);
  const [backingUp, setBackingUp] = useState(false);

  const handleRecover = async () => {
    setRecovering(true);
    try {
      const res = await axios.post(`${API}/admin/migrate-files-to-gridfs`, {}, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      const { migrated_media, migrated_documents, skipped_already_in_gridfs, errors } = res.data;
      if (migrated_media + migrated_documents > 0) {
        toast.success(`Recovered ${migrated_media} images and ${migrated_documents} documents to durable storage`);
      } else if (skipped_already_in_gridfs > 0) {
        toast.info(`All ${skipped_already_in_gridfs} files already in durable storage — no recovery needed`);
      } else {
        toast.info('No files to recover');
      }
      if (errors?.length) {
        console.warn('Recovery errors:', errors);
      }
      fetchFiles();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Recovery failed');
    } finally {
      setRecovering(false);
    }
  };

  const handleBackup = async () => {
    setBackingUp(true);
    try {
      const res = await axios.get(`${API}/admin/backup/library?kind=media`, {
        headers: { 'Authorization': `Bearer ${user.token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `media_backup_${new Date().toISOString().slice(0,10)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Media backup downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Backup failed');
    } finally {
      setBackingUp(false);
    }
  };

  // In-app download — hides backend URL from address bar
  const downloadInApp = async (file) => {
    try {
      const res = await axios.get(`${API}/media/file/${file.filename}`, {
        headers: { 'Authorization': `Bearer ${user.token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: file.file_type || 'application/octet-stream' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = file.original_name || file.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Could not download file');
    }
  };

  const fetchFiles = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory !== 'all') params.append('category', selectedCategory);
      if (searchTerm) params.append('search', searchTerm);
      
      const response = await axios.get(`${API}/media/files?${params}`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      setFiles(response.data.files || []);
    } catch (error) {
      toast.error('Failed to load media files');
    } finally {
      setLoading(false);
    }
  }, [user.token, selectedCategory, searchTerm]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/media/categories`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      setCategories(response.data.categories || []);
    } catch (error) {
      console.log('Could not fetch categories');
    }
  };

  useEffect(() => {
    fetchFiles();
    fetchCategories();
  }, [fetchFiles]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Invalid file type. Please upload JPEG, PNG, GIF, WebP, or SVG');
        return;
      }
      
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File too large. Maximum size is 10MB');
        return;
      }
      
      setUploadFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => setUploadPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('category', uploadCategory);
      formData.append('description', uploadDescription);
      formData.append('tags', uploadTags);

      await axios.post(`${API}/media/upload`, formData, {
        headers: { 
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('File uploaded successfully');
      setShowUploadModal(false);
      resetUploadForm();
      fetchFiles();
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const resetUploadForm = () => {
    setUploadFile(null);
    setUploadPreview(null);
    setUploadCategory('product');
    setUploadDescription('');
    setUploadTags('');
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;

    try {
      await axios.delete(`${API}/media/files/${fileId}`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      toast.success('File deleted');
      fetchFiles();
      fetchCategories();
      setSelectedFile(null);
    } catch (error) {
      toast.error('Failed to delete file');
    }
  };

  const copyToClipboard = (url) => {
    const fullUrl = `${BACKEND_URL}${url}`;
    navigator.clipboard.writeText(fullUrl);
    setCopiedUrl(url);
    toast.success('URL copied to clipboard');
    setTimeout(() => setCopiedUrl(null), 2000);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const categoryOptions = [
    { value: 'product', label: 'Product Images' },
    { value: 'slide', label: 'Slides/Presentations' },
    { value: 'banner', label: 'Banners' },
    { value: 'logo', label: 'Logos' },
    { value: 'icon', label: 'Icons' },
    { value: 'general', label: 'General' }
  ];

  return (
    <div className="p-3 sm:p-6 space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Media Library</h2>
          <p className="text-slate-500">Manage product images and visual assets</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={handleRecover}
            disabled={recovering}
            variant="outline"
            className="border-amber-300 text-amber-700 hover:bg-amber-50"
            data-testid="recover-media-btn"
            title="Move legacy local files into durable MongoDB storage"
          >
            <ShieldCheck className="w-4 h-4 mr-2" />
            {recovering ? 'Recovering…' : 'Recover Files'}
          </Button>
          <Button
            onClick={handleBackup}
            disabled={backingUp}
            variant="outline"
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            data-testid="backup-media-btn"
            title="Download a ZIP of all media files"
          >
            <Archive className="w-4 h-4 mr-2" />
            {backingUp ? 'Preparing…' : 'Backup ZIP'}
          </Button>
          <Button
            onClick={() => setShowUploadModal(true)}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="upload-media-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload Image
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 bg-white p-4 rounded-xl border border-slate-200">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            type="text"
            placeholder="Search files..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
        
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
        >
          <option value="all">All Categories</option>
          {categoryOptions.map(cat => (
            <option key={cat.value} value={cat.value}>{cat.label}</option>
          ))}
        </select>

        <div className="flex bg-slate-100 rounded-lg p-1">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded ${viewMode === 'grid' ? 'bg-white shadow-sm' : ''}`}
          >
            <Grid className="w-4 h-4" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded ${viewMode === 'list' ? 'bg-white shadow-sm' : ''}`}
          >
            <List className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Stats */}
      {categories.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {categories.map(cat => (
            <span 
              key={cat.name}
              className={`px-3 py-1 rounded-full text-xs font-medium cursor-pointer transition-colors
                ${selectedCategory === cat.name 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              onClick={() => setSelectedCategory(selectedCategory === cat.name ? 'all' : cat.name)}
            >
              {cat.name} ({cat.count})
            </span>
          ))}
        </div>
      )}

      {/* Files Grid/List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : files.length === 0 ? (
        <div className="text-center py-20 bg-slate-50 rounded-xl border border-dashed border-slate-300">
          <Image className="w-12 h-12 mx-auto text-slate-400 mb-4" />
          <p className="text-slate-600 font-medium">No media files yet</p>
          <p className="text-slate-400 text-sm mt-1">Upload your first image to get started</p>
          <Button
            onClick={() => setShowUploadModal(true)}
            variant="outline"
            className="mt-4"
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload Image
          </Button>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
          {files.map((file) => (
            <div
              key={file.id}
              className="group relative bg-white rounded-lg border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => setSelectedFile(file)}
              data-testid={`media-tile-${file.id}`}
            >
              <div className="aspect-square bg-slate-100">
                <img
                  src={`${BACKEND_URL}${file.url}`}
                  alt={file.original_name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23f1f5f9" width="100" height="100"/><text x="50" y="50" text-anchor="middle" dy=".3em" fill="%2394a3b8" font-size="12">No Image</text></svg>';
                  }}
                />
              </div>
              <div className="p-2">
                <p className="text-xs font-medium text-slate-700 truncate">{file.original_name}</p>
                <p className="text-xs text-slate-400">{formatFileSize(file.file_size)}</p>
              </div>
              {/* Hover actions - corner delete button only */}
              <button
                onClick={(e) => { e.stopPropagation(); handleDelete(file.id); }}
                className="absolute top-2 right-2 p-1.5 bg-white/90 backdrop-blur-sm rounded-full hover:bg-red-50 text-red-600 opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                title="Delete"
                data-testid={`media-tile-delete-${file.id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
              {/* Category badge */}
              <span className="absolute top-2 left-2 px-2 py-0.5 bg-black/50 text-white text-xs rounded">
                {file.category}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Preview</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Category</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Size</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Uploaded</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {files.map((file) => (
                <tr key={file.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2">
                    <img
                      src={`${BACKEND_URL}${file.url}`}
                      alt={file.original_name}
                      className="w-12 h-12 object-cover rounded"
                    />
                  </td>
                  <td className="px-4 py-2">
                    <p className="font-medium text-slate-700">{file.original_name}</p>
                    {file.description && (
                      <p className="text-xs text-slate-400">{file.description}</p>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded">{file.category}</span>
                  </td>
                  <td className="px-4 py-2 text-sm text-slate-600">{formatFileSize(file.file_size)}</td>
                  <td className="px-4 py-2 text-sm text-slate-600">
                    {new Date(file.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setSelectedFile(file)}
                        className="p-1.5 hover:bg-slate-100 rounded"
                        title="View"
                      >
                        <Eye className="w-4 h-4 text-slate-600" />
                      </button>
                      <button
                        onClick={() => handleDelete(file.id)}
                        className="p-1.5 hover:bg-red-50 rounded"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
            <div className="p-6 border-b border-slate-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-slate-800">Upload Media</h3>
              <button onClick={() => { setShowUploadModal(false); resetUploadForm(); }}>
                <X className="w-5 h-5 text-slate-400 hover:text-slate-600" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* File dropzone */}
              <div 
                className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors
                  ${uploadPreview ? 'border-blue-300 bg-blue-50' : 'border-slate-300 hover:border-blue-400'}`}
                onClick={() => document.getElementById('media-file-input').click()}
              >
                {uploadPreview ? (
                  <div className="space-y-2">
                    <img src={uploadPreview} alt="Preview" className="max-h-40 mx-auto rounded" />
                    <p className="text-sm text-slate-600">{uploadFile?.name}</p>
                    <p className="text-xs text-slate-400">{formatFileSize(uploadFile?.size || 0)}</p>
                  </div>
                ) : (
                  <>
                    <Upload className="w-10 h-10 mx-auto text-slate-400 mb-2" />
                    <p className="text-slate-600">Click to select or drag & drop</p>
                    <p className="text-xs text-slate-400">JPEG, PNG, GIF, WebP, SVG (max 10MB)</p>
                  </>
                )}
                <input
                  id="media-file-input"
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
                <select
                  value={uploadCategory}
                  onChange={(e) => setUploadCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                >
                  {categoryOptions.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description (optional)</label>
                <Input
                  type="text"
                  placeholder="Brief description of the image"
                  value={uploadDescription}
                  onChange={(e) => setUploadDescription(e.target.value)}
                />
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Tags (optional)</label>
                <Input
                  type="text"
                  placeholder="Comma-separated tags (e.g., medicine, tablet, healthcare)"
                  value={uploadTags}
                  onChange={(e) => setUploadTags(e.target.value)}
                />
              </div>
            </div>

            <div className="p-6 bg-slate-50 border-t border-slate-200 flex justify-end gap-3">
              <Button variant="outline" onClick={() => { setShowUploadModal(false); resetUploadForm(); }}>
                Cancel
              </Button>
              <Button 
                onClick={handleUpload} 
                disabled={!uploadFile || uploading}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* File Detail Modal */}
      {selectedFile && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="media-file-details-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-slate-800">File Details</h3>
              <button onClick={() => setSelectedFile(null)}>
                <X className="w-5 h-5 text-slate-400 hover:text-slate-600" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="bg-slate-100 rounded-xl p-4 mb-6">
                <img
                  src={`${BACKEND_URL}${selectedFile.url}`}
                  alt={selectedFile.original_name}
                  className="max-h-80 mx-auto rounded"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Filename:</span>
                  <p className="font-medium">{selectedFile.original_name}</p>
                </div>
                <div>
                  <span className="text-slate-500">Size:</span>
                  <p className="font-medium">{formatFileSize(selectedFile.file_size)}</p>
                </div>
                <div>
                  <span className="text-slate-500">Category:</span>
                  <p className="font-medium">{selectedFile.category}</p>
                </div>
                <div>
                  <span className="text-slate-500">Type:</span>
                  <p className="font-medium">{selectedFile.file_type}</p>
                </div>
                <div>
                  <span className="text-slate-500">Uploaded by:</span>
                  <p className="font-medium">{selectedFile.uploaded_by}</p>
                </div>
                <div>
                  <span className="text-slate-500">Uploaded:</span>
                  <p className="font-medium">{new Date(selectedFile.created_at).toLocaleString()}</p>
                </div>
                {selectedFile.description && (
                  <div className="col-span-2">
                    <span className="text-slate-500">Description:</span>
                    <p className="font-medium">{selectedFile.description}</p>
                  </div>
                )}
                {selectedFile.tags?.length > 0 && (
                  <div className="col-span-2">
                    <span className="text-slate-500">Tags:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {selectedFile.tags.map((tag, i) => (
                        <span key={i} className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="p-6 bg-slate-50 border-t border-slate-200 flex justify-between">
              <Button 
                variant="outline" 
                className="text-red-600 border-red-300 hover:bg-red-50"
                onClick={() => { handleDelete(selectedFile.id); }}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setSelectedFile(null)}>
                  Close
                </Button>
                <Button
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={() => downloadInApp(selectedFile)}
                  data-testid="media-modal-download"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaLibrary;
